import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { optionalEmailSchema } from '@/lib/validations'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { userService } from '@/services/user_service'
import { useAuth } from '@/hooks/useAuth'
import { useMyPermissions } from '@/hooks/useMyPermissions'
import { useMe } from '@/hooks/useMe'
import { useViewMode } from '@/stores/viewMode'
import { AllowedAction } from '@/data/allowedActions'
import { PasswordResetSection } from '@/components/PasswordResetSection'
import { toast } from 'sonner'
import { ClinicUser } from '@magnetar/data-access'
import { Clinic, User } from '@magnetar/admin-api-interfaces'

const formSchema = z.object({
  account: z.string().min(1, { message: '請輸入使用者帳號' }),
  user_name: z.string().min(1, { message: '請輸入使用者名稱' }),
  email: optionalEmailSchema(),
})

type FormValues = z.infer<typeof formSchema>

interface EditClinicUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicUser: ClinicUser & { user: User; clinic: Clinic }
  onSuccess: () => void
}

export function EditClinicUserDialog({
  open,
  onOpenChange,
  clinicUser,
  onSuccess,
}: EditClinicUserDialogProps) {
  const { token } = useAuth()
  const { hasPermission } = useMyPermissions()
  const { user } = useMe()
  const { mode } = useViewMode()
  const hasEmail = !!clinicUser.user?.email
  const [pendingPassword, setPendingPassword] = useState<string | undefined>(undefined)

  // 階層權限檢查：sys_admin > provider (provider_clinic_management) > clinic (clinic_user_management)
  const canResetPassword = (() => {
    if (user?.role_type === 'sys_admin') return true
    if (hasPermission('provider_clinic_management', AllowedAction.U)) return true
    if (mode === 'clinic' && hasPermission('clinic_user_management', AllowedAction.U)) return true
    return false
  })()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      account: clinicUser.user?.account || '',
      user_name: clinicUser.user?.user_name || '',
      email: clinicUser.user?.email || '',
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        account: clinicUser.user?.account || '',
        user_name: clinicUser.user?.user_name || '',
        email: clinicUser.user?.email || '',
      })
      setPendingPassword(undefined)
    }
  }, [open, clinicUser, form])

  const onSubmit = async (values: FormValues) => {
    if (!token) return

    const data: {
      account: string
      user_name: string
      email?: string | null
      is_active: boolean
      password?: string
    } = {
      account: values.account,
      user_name: values.user_name,
      email: hasEmail ? clinicUser.user.email : (values.email || null),
      is_active: clinicUser.user?.is_active ?? true,
    }

    if (pendingPassword) {
      data.password = pendingPassword
    }

    const result = await userService.updateUser(clinicUser.user_id, data, token)

    if (result.success) {
      toast.success('更新使用者成功')
      onOpenChange(false)
      onSuccess()
    } else {
      toast.error(result.error?.message || '更新使用者失敗')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>編輯使用者</DialogTitle>
          <DialogDescription>編輯使用者基本資料</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者帳號</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入使用者帳號" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入使用者名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="example@domain.com"
                      disabled={hasEmail}
                      {...field}
                    />
                  </FormControl>
                  {hasEmail && (
                    <p className="text-xs text-muted-foreground">Email 已設定，無法修改</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <PasswordResetSection
              canResetPassword={canResetPassword}
              onPasswordChange={setPendingPassword}
            />
            <DialogFooter>
              <Button type="submit">儲存</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
